import { useEffect, useState, useCallback } from "react";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import Tooltip from "@mui/material/Tooltip";
import { ingestAnalyticsEvent, fetchTalentAnalytics } from "services/network/talentAnalytics";

import "components/Screens/Marketplace/Customer/ProfileSection/ShortlistButton.scss";

interface ShortlistButtonProps {
  talentId?: string;
}

/**
 * Circular heart toggle that lets a customer shortlist a talent. Current state is
 * derived from the customer-scoped analytics (currentlyShortlisted > 0); toggling
 * emits a SHORTLIST_ADDED / SHORTLIST_REMOVED event. The toggle is optimistic and
 * reverts if the event fails to send.
 */
function ShortlistButton({ talentId }: ShortlistButtonProps) {
  const [isShortlisted, setIsShortlisted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!talentId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const data = await fetchTalentAnalytics(talentId, { window: "all", metrics: "shortlists" });
      if (!cancelled) {
        setIsShortlisted((data?.shortlists?.currentlyShortlisted ?? 0) > 0);
        setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [talentId]);

  const handleToggle = useCallback(async () => {
    if (!talentId || submitting) return;
    const next = !isShortlisted;
    setIsShortlisted(next); // optimistic
    setSubmitting(true);
    try {
      await ingestAnalyticsEvent({
        eventType: next ? "SHORTLIST_ADDED" : "SHORTLIST_REMOVED",
        talentId,
        source: "PORTAL",
      });
    } catch {
      setIsShortlisted(!next); // revert on failure
    } finally {
      setSubmitting(false);
    }
  }, [talentId, isShortlisted, submitting]);

  const label = isShortlisted ? "Remove from shortlist" : "Add to shortlist";

  return (
    <Tooltip title={label}>
      <button
        type="button"
        className={`shortlist-button${isShortlisted ? " shortlist-button--active" : ""}`}
        onClick={handleToggle}
        disabled={loading || submitting || !talentId}
        aria-pressed={isShortlisted}
        aria-label={label}
      >
        {isShortlisted ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
      </button>
    </Tooltip>
  );
}

export default ShortlistButton;
